import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import '../../styles/professional-theme.css';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Ship, PackageCheck, Calendar, Search, X, RefreshCw, History, Users, ClipboardCheck, Send } from 'lucide-react';
import { CountryRoute } from '../../components/CountryDisplay';
import { SHIPMENT_STATUS_LABELS } from '../../lib/constants';
import { useLanguage } from '../../contexts/LanguageContext';
import ShipmentScanDialog from '../../components/ShipmentScanDialog';
import EmptyState from '../../components/EmptyState';

const HISTORY_STATUSES = ['completed', 'cancelled'];
const LOADABLE_STATUSES = ['planned', 'scheduled'];

const statusAccent = (status) => {
  switch (status) {
    case 'completed':
    case 'arrived': return 'bg-green-500';
    case 'in_transit':
    case 'in_progress': return 'bg-accent';
    case 'cancelled': return 'bg-red-500';
    case 'delayed':
    case 'awaiting_delivery': return 'bg-amber-500';
    default: return 'bg-slate-400';
  }
};

const LoaderDashboard = () => {
  const { language } = useLanguage();
  const [shipments, setShipments] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [autoMode, setAutoMode] = useState(false);
  const [query, setQuery] = useState('');
  const [scan, setScan] = useState({ open: false, shipmentId: null, autoAssign: false });
  const [selected, setSelected] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [shipRes, grpRes] = await Promise.all([
        api.get('/api/shipments'),
        api.get('/api/packages/grouped-by-partner'),
      ]);
      setShipments(shipRes.data || []);
      setGrouped(grpRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Loading error' : 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchData();
    api.get('/api/company/transport-modes')
      .then(r => setAutoMode((r.data?.loading_mode || 'manual') === 'auto'))
      .catch(() => {});
  }, [fetchData]);

  const label = (s) => (SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[s] || s;

  const { active, history } = useMemo(() => {
    const q = query.toLowerCase().trim();
    const match = (s) => !q || s.name?.toLowerCase().includes(q)
      || s.origin_country?.toLowerCase().includes(q) || s.destination_country?.toLowerCase().includes(q)
      || s.flight_name?.toLowerCase().includes(q);
    const filtered = shipments.filter(match);
    const byDeparture = (a, b) => {
      const da = a.departure_date ? new Date(a.departure_date).getTime() : Infinity;
      const db = b.departure_date ? new Date(b.departure_date).getTime() : Infinity;
      return da - db;
    };
    return {
      active: filtered.filter(s => !HISTORY_STATUSES.includes(s.status)).sort(byDeparture),
      history: filtered.filter(s => HISTORY_STATUSES.includes(s.status)),
    };
  }, [shipments, query]);

  const totalPending = grouped.reduce((n, g) => n + (g.package_count || 0), 0);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleGroup = (pkgs) => setSelected(prev => {
    const next = new Set(prev);
    const allIn = pkgs.every(p => next.has(p.id));
    pkgs.forEach(p => allIn ? next.delete(p.id) : next.add(p.id));
    return next;
  });

  const assignableDepartures = useMemo(
    () => shipments.filter(s => LOADABLE_STATUSES.includes(s.status)),
    [shipments],
  );

  const handleAssign = async (shipmentId) => {
    setAssigning(true);
    setError(''); setMsg('');
    try {
      await api.post('/api/shipments/bulk-add-packages', {
        shipment_id: shipmentId,
        package_ids: Array.from(selected),
      });
      setMsg(`${selected.size} colis assigné(s) au départ.`);
      setSelected(new Set());
      setAssignOpen(false);
      fetchData();
      setTimeout(() => setMsg(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Assignment error' : "Erreur d'assignation"));
    } finally {
      setAssigning(false);
    }
  };

  const openScan = (shipmentId, autoAssign) => setScan({ open: true, shipmentId, autoAssign });

  const renderDepartureCard = (s, { chargeable }) => (
    <div key={s.id} className="flex overflow-hidden rounded-lg border border-border bg-card">
      <span className={`w-1.5 shrink-0 ${statusAccent(s.status)}`} aria-hidden="true" />
      <div className="flex-1 min-w-0 p-3.5 sm:p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            <Ship className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-semibold text-sm sm:text-base text-foreground truncate">{s.name}</span>
            <Badge variant="secondary" className="text-xs">{label(s.status)}</Badge>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {chargeable && autoMode && (
              <Button size="sm" variant="outline" onClick={() => openScan(s.id, true)}>
                <PackageCheck className="h-4 w-4 mr-1.5" />{language === 'en' ? 'Load (scan)' : 'Charger (scan)'}
              </Button>
            )}
            <Button size="sm" onClick={() => openScan(s.id, false)}>
              <ClipboardCheck className="h-4 w-4 mr-1.5" />{language === 'en' ? 'Verify' : 'Vérifier'}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1.5 flex-wrap">
          <CountryRoute origin={s.origin_country} destination={s.destination_country} size="sm" />
          {s.departure_date && (
            <span className="flex items-center gap-1 text-xs">
              <Calendar className="h-3 w-3" />{new Date(s.departure_date).toLocaleDateString('fr-FR')}
            </span>
          )}
          <span className="text-muted-foreground/40">·</span>
          <span className="text-xs">{s.package_count || 0} {language === 'en' ? 'packages' : 'colis'}</span>
        </div>
      </div>
    </div>
  );

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <PackageCheck className="h-6 w-6 text-accent" />
            {language === 'en' ? 'Loading' : 'Chargement'}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {language === 'en'
              ? 'Assign packages to a departure, then verify loading (missing packages).'
              : 'Assignez les colis à un départ, puis vérifiez le chargement (colis manquants).'}
          </p>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {msg && <Alert className="bg-green-50 border-green-200"><AlertDescription className="text-green-800">{msg}</AlertDescription></Alert>}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" /> {language === 'en' ? 'Loading...' : 'Chargement...'}
          </div>
        ) : (
          <Tabs defaultValue="packages" className="w-full">
            <TabsList>
              <TabsTrigger value="packages">
                <Users className="h-4 w-4 mr-1.5" />{language === 'en' ? 'Packages by partner' : 'Colis par partenaire'} ({totalPending})
              </TabsTrigger>
              <TabsTrigger value="active">
                <Ship className="h-4 w-4 mr-1.5" />{language === 'en' ? 'Departures' : 'Départs'} ({active.length})
              </TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-1.5" />{language === 'en' ? 'History' : 'Historique'} ({history.length})
              </TabsTrigger>
            </TabsList>

            {/* Colis par partenaire + assignation */}
            <TabsContent value="packages" className="space-y-3 mt-4">
              {grouped.length === 0 ? (
                <EmptyState icon={Users} title={language === 'en' ? 'No pending package' : 'Aucun colis en attente'}
                  description={language === 'en' ? 'Packages to assign will appear here.' : 'Les colis à assigner apparaîtront ici.'} />
              ) : (
                grouped.map(g => {
                  const pkgs = g.packages || [];
                  const allIn = pkgs.length > 0 && pkgs.every(p => selected.has(p.id));
                  return (
                    <div key={g.partner_id} className="rounded-lg border border-border bg-card overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border">
                        <Checkbox checked={allIn} onCheckedChange={() => toggleGroup(pkgs)} />
                        <span className="font-medium text-sm truncate">{g.partner_name}</span>
                        <Badge variant="secondary" className="text-xs ml-auto shrink-0">{g.package_count} {language === 'en' ? 'pkg' : 'colis'}</Badge>
                      </div>
                      <div className="divide-y divide-border">
                        {pkgs.map(p => (
                          <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30">
                            <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                            <span className="font-mono text-xs shrink-0">{p.oce_code}</span>
                            <span className="text-xs text-muted-foreground truncate">
                              {p.recipient_name} · {[p.origin_country, p.destination_country].filter(Boolean).join(' → ')}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="active" className="space-y-3 mt-4">
              {active.length === 0 ? (
                <EmptyState icon={Ship} title={language === 'en' ? 'No departure' : 'Aucun départ'}
                  description={language === 'en' ? 'Departures created by an administrator will appear here.' : 'Les départs créés par un administrateur apparaîtront ici.'} />
              ) : (
                active.map(s => renderDepartureCard(s, { chargeable: LOADABLE_STATUSES.includes(s.status) }))
              )}
            </TabsContent>

            <TabsContent value="history" className="space-y-3 mt-4">
              {history.length === 0 ? (
                <EmptyState icon={History} title={language === 'en' ? 'No history' : 'Aucun historique'}
                  description={language === 'en' ? 'Completed or cancelled departures will appear here.' : 'Les départs terminés ou annulés apparaîtront ici.'} />
              ) : (
                history.map(s => renderDepartureCard(s, { chargeable: false }))
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Barre d'assignation flottante */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 z-40 flex items-center gap-3 rounded-lg border border-border bg-card shadow-lg px-4 py-3">
          <span className="text-sm font-medium">{selected.size} {language === 'en' ? 'selected' : 'sélectionné(s)'}</span>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>{language === 'en' ? 'Clear' : 'Effacer'}</Button>
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            <Send className="h-4 w-4 mr-1.5" />{language === 'en' ? 'Assign to a departure' : 'Assigner à un départ'}
          </Button>
        </div>
      )}

      {/* Choix du départ pour l'assignation */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{language === 'en' ? 'Assign to a departure' : 'Assigner à un départ'}</DialogTitle>
            <DialogDescription>{selected.size} {language === 'en' ? 'package(s) selected' : 'colis sélectionné(s)'}</DialogDescription>
          </DialogHeader>
          {assignableDepartures.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {language === 'en' ? 'No planned departure available.' : 'Aucun départ planifié disponible.'}
            </p>
          ) : (
            <div className="space-y-2">
              {assignableDepartures.map(s => (
                <button key={s.id} type="button" disabled={assigning}
                  onClick={() => handleAssign(s.id)}
                  className="w-full text-left rounded-lg border border-border p-3 hover:border-accent hover:bg-accent/5 transition-all disabled:opacity-50">
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {[s.origin_country, s.destination_country].filter(Boolean).join(' → ')}
                    {s.departure_date ? ` · ${new Date(s.departure_date).toLocaleDateString('fr-FR')}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {scan.shipmentId && (
        <ShipmentScanDialog
          open={scan.open}
          onOpenChange={(open) => {
            setScan((prev) => ({ ...prev, open }));
            if (!open) fetchData();
          }}
          shipmentId={scan.shipmentId}
          checkpoint="loading"
          autoAssign={scan.autoAssign}
          onScanned={fetchData}
        />
      )}
    </ProfessionalLayout>
  );
};

export default LoaderDashboard;
